import { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { HTTP_STATUS_UNAUTHORIZED } from '@/lib/constants';

const AuthContext = createContext(null);

// ===== Module-level helpers — pure, stable identities =====

const extractErrorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return 'Login failed. Please try again.';
};

const onBootstrapError = (err, setUser) => {
  if (err.response?.status !== HTTP_STATUS_UNAUTHORIZED) {
    toast.error('Unable to verify session. Please try again.');
  }
  setUser(false);
};

const bootstrapSession = (setUser, setLoading) =>
  apiClient.get('/auth/me')
    .then(r => setUser(r.data))
    .catch(e => onBootstrapError(e, setUser))
    .finally(() => setLoading(false));

const runLogin = (email, password, setUser) =>
  apiClient.post('/auth/login', { email, password })
    .then(r => { setUser(r.data); return { success: true }; })
    .catch(e => ({ success: false, error: extractErrorMessage(e) }));

const runLogout = (setUser) =>
  apiClient.post('/auth/logout', {})
    .catch(() => toast.warning('Could not reach server. Logged out locally.'))
    .finally(() => setUser(false));

// ===== Component =====

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { bootstrapSession(setUser, setLoading);
  }, [setUser, setLoading, bootstrapSession]);

  const login = useCallback(
    (email, password) => runLogin(email, password, setUser),
    [setUser, runLogin]
  );
  const logout = useCallback(
    () => runLogout(setUser),
    [setUser, runLogout]
  );

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
